<template>
  <div
    class="player-avatar"
    :class="[`player-avatar--${shape}`, { 'player-avatar--ring': ring, 'player-avatar--glow': glow }]"
    :style="rootStyle"
    role="img"
    :aria-label="name || 'Аватар игрока'"
  >
    <div class="player-avatar__inner">
      <img
        v-if="src && !failed"
        class="player-avatar__img"
        :src="src"
        :alt="name || ''"
        loading="lazy"
        decoding="async"
        draggable="false"
        @error="failed = true"
      />
      <div v-else class="player-avatar__fallback" :style="{ background: gradient }">
        <span class="player-avatar__initials">{{ initials }}</span>
      </div>
    </div>
    <span v-if="corners" class="player-avatar__corners" aria-hidden="true">
      <span class="player-avatar__corner player-avatar__corner--tl"></span>
      <span class="player-avatar__corner player-avatar__corner--tr"></span>
      <span class="player-avatar__corner player-avatar__corner--bl"></span>
      <span class="player-avatar__corner player-avatar__corner--br"></span>
    </span>
  </div>
</template>

<script setup>
/**
 * PlayerAvatar — аватар игрока: фото или инициалы на детерминированном градиенте (по имени).
 *
 * <PlayerAvatar :src="me.avatarUrl" :name="me.playerName" :size="112" ring />             // экран 1
 * <PlayerAvatar :src="…" :name="…" :size="120" shape="rounded" corners />                 // экран 8
 * <PlayerAvatar :name="row.playerName" :size="48" />                                      // строка лидерборда
 *
 * props: src (url|null), name, size (px, 56), shape ('circle'|'rounded'),
 *        corners (лаймовые L-уголки), ring (тонкое кольцо), glow (лаймовое свечение).
 */
import { computed, ref, watch } from "vue";
import { avatarGradient, initialsOf } from "@/utils/player";

const props = defineProps({
  src: { type: String, default: null },
  name: { type: String, default: "" },
  size: { type: Number, default: 56 },
  shape: { type: String, default: "circle", validator: (v) => ["circle", "rounded"].includes(v) },
  corners: { type: Boolean, default: false },
  ring: { type: Boolean, default: false },
  glow: { type: Boolean, default: false },
});

const failed = ref(false);
watch(
  () => props.src,
  () => {
    failed.value = false;
  },
);

const initials = computed(() => initialsOf(props.name));
const gradient = computed(() => avatarGradient(props.name || "?"));

const rootStyle = computed(() => {
  const s = props.size;
  return {
    width: `${s}px`,
    height: `${s}px`,
    "--pa-radius": props.shape === "circle" ? "50%" : `${Math.round(s * 0.2)}px`,
    "--pa-font": `${Math.max(12, Math.round(s * 0.38))}px`,
    "--pa-corner": `${Math.max(8, Math.round(s * 0.2))}px`,
    "--pa-corner-inset": `${Math.max(4, Math.round(s * 0.11))}px`,
    "--pa-corner-thick": `${s >= 80 ? 3 : 2}px`,
  };
});
</script>

<style scoped>
.player-avatar {
  position: relative;
  flex-shrink: 0;
  border-radius: var(--pa-radius);
}

.player-avatar__inner {
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: inherit;
  background: var(--cb-surface-3);
}

.player-avatar--ring .player-avatar__inner {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.14), 0 0 0 4px rgba(221, 245, 90, 0.08);
}

.player-avatar--rounded .player-avatar__inner {
  box-shadow: inset 0 0 0 1px var(--cb-border-strong);
}

.player-avatar--glow .player-avatar__inner {
  box-shadow: 0 0 0 2px var(--cb-lime), var(--cb-glow);
}

.player-avatar__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  -webkit-user-drag: none;
}

.player-avatar__fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.player-avatar__initials {
  font-size: var(--pa-font);
  font-weight: 800;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.02em;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.35);
  -webkit-user-select: none;
  user-select: none;
}

.player-avatar__corners {
  position: absolute;
  inset: var(--pa-corner-inset);
  pointer-events: none;
}

.player-avatar__corner {
  position: absolute;
  width: var(--pa-corner);
  height: var(--pa-corner);
  border: 0 solid var(--cb-lime);
  filter: drop-shadow(0 0 3px rgba(221, 245, 90, 0.5));
}

.player-avatar__corner--tl {
  top: 0;
  left: 0;
  border-top-width: var(--pa-corner-thick);
  border-left-width: var(--pa-corner-thick);
  border-top-left-radius: 5px;
}

.player-avatar__corner--tr {
  top: 0;
  right: 0;
  border-top-width: var(--pa-corner-thick);
  border-right-width: var(--pa-corner-thick);
  border-top-right-radius: 5px;
}

.player-avatar__corner--bl {
  bottom: 0;
  left: 0;
  border-bottom-width: var(--pa-corner-thick);
  border-left-width: var(--pa-corner-thick);
  border-bottom-left-radius: 5px;
}

.player-avatar__corner--br {
  bottom: 0;
  right: 0;
  border-bottom-width: var(--pa-corner-thick);
  border-right-width: var(--pa-corner-thick);
  border-bottom-right-radius: 5px;
}
</style>
